/**
 * ★ **채널 전환의 마지막 프레임 잡기** (v28, 2026-09-05 무대 요청).
 *
 * ── 무엇을 고치는가 ─────────────────────────────────────────
 *
 * 채널 전환에는 **화면이 비는 창**이 있다 — 실측 **97 · 126 · 351ms**. `ChannelGate` 가
 * 규칙 27 대로 한 렌더를 비우고(`<div className="stage" />`), 새 화면이 서서 매니페스트를
 * 받아 첫 프레임을 붙일 때까지다. 그동안 시청자가 보는 것은 **검은 화면**이고, 그것이
 * 「전환이 느리다」로 읽힌다.
 *
 * **전환을 빠르게 만드는 것이 아니다** — 그 창은 망과 기기가 정한다. 대신 그 창을
 * **직전 채널의 마지막 프레임으로 덮는다.**
 *
 * ── 왜 캔버스인가 ───────────────────────────────────────────
 *
 * `<video>` 를 전환 동안 살려 두는 안(무대를 `ChannelGate` 위로 끌어올리는 것)은
 * **규칙 27·72 와 어댑터를 동시에 건드린다** — 한 쌍의 소유·`key` 재마운트·게이트 판정이
 * 전부 그 구조 위에 서 있다. 캔버스는 **그리는 순간의 사본**이라 그 어느 것도 안 건드린다.
 *
 * ★★ **그리고 읽지 않는다.** `toDataURL()`·`getImageData()` 를 쓰지 말 것 — 아이폰의
 * 네이티브 HLS 는 **교차 출처 영상을 그대로 재생**하므로 그 캔버스는 오염(tainted)되고,
 * **읽는 순간 `SecurityError` 로 던진다.** 그리기와 보여 주기는 오염돼도 된다. 그래서
 * 이 모듈은 **캔버스 엘리먼트 자체를 화면에 붙이고**, 데이터 URL 을 만들지 않는다.
 * 노트북에서는 hls.js 가 MSE(같은 출처 blob)를 쓰므로 **오염이 아예 안 나 이 함정이
 * 로컬에서는 영원히 안 보인다.**
 */

/** 잡은 프레임을 최대 얼마나 들고 있는가. 넘으면 놓는다 — **얼어붙은 화면은 거짓말이다** */
export const FREEZE_CAP_MS = 2000;

/**
 * 어느 버퍼를 그릴 것인가 (순수).
 *
 * **보이는 것 하나를 고른다** — 어댑터가 대기 버퍼를 `opacity: 0` 으로 두므로
 * (`hls-player.ts` 의 `swap`) 그 값이 판정이다. 둘 다 보이는 순간은 없다.
 * 그림이 없는 것(`videoWidth === 0`)과 아직 프레임이 없는 것(`readyState < 2`)은 고르지
 * 않는다 — 그리면 **검은 사각형**이 되고, 그것은 안 그리는 것보다 나쁘다(고쳤다고
 * 착각하게 만든다).
 */
export interface FreezeSource {
  /** 인라인 `style.opacity`. 어댑터가 `'0'`/`'1'` 로만 쓴다 */
  opacity: string;
  videoWidth: number;
  readyState: number;
  paused: boolean;
}

export function pickFreezeSource(list: readonly FreezeSource[]): number | null {
  for (const [index, s] of list.entries()) {
    if (s.opacity === '0') continue;
    if (s.videoWidth <= 0 || s.readyState < 2) continue;
    // **멈춘 것도 그린다** — 광고 사이·스왑 직후에 잠깐 `paused` 인 프레임이 있고,
    // 그 그림은 화면에 실제로 떠 있던 것이다. `paused` 는 고를 때의 조건이 아니다.
    return index;
  }
  return null;
}

/**
 * 놓을 때가 됐는가 (순수).
 *
 * 놓는 이유가 둘이고 **둘 다 있어야 한다** — 새 영상이 실제로 그려지기 시작했거나,
 * 상한을 넘었거나. 앞엣것만 두면 새 채널이 영영 안 뜰 때 **얼어붙은 그림이 남아
 * 「재생 중」으로 보이고**, 뒤엣것만 두면 상한이 곧 전환 시간이 된다.
 */
export function shouldReleaseFreeze(o: { painting: boolean; heldMs: number }): boolean {
  return o.painting || o.heldMs >= FREEZE_CAP_MS;
}

/* ── 여기서부터 DOM. 위 둘이 판정을 전부 갖는다 ───────────────── */

let canvas: HTMLCanvasElement | null = null;
let active = false;
let heldSince = 0;

function surface(): HTMLCanvasElement {
  if (canvas === null) {
    canvas = document.createElement('canvas');
    // `object-fit` 은 `<canvas>` 에도 적용된다(대체 요소다) — `<video>` 와 **같은 값**을
    // 줘야 레터박스가 어긋나지 않는다.
    canvas.style.cssText =
      'position:absolute;top:0;left:0;width:100%;height:100%;' +
      // 영상(z 1~2) 위, 오버레이 층(z 10) 아래. 그래서 오류·게이트·시트가 이것을 덮는다.
      'object-fit:contain;background:#000;z-index:3;pointer-events:none;';
  }
  return canvas;
}

/**
 * 지금 화면에 있는 프레임을 잡는다. **전환을 부르기 직전에** 부른다 — 그 뒤에는
 * 옛 화면이 내려가 `<video>` 가 문서에서 떨어진다.
 *
 * 실패하면(첫 진입·아직 프레임 없음) **들고 있던 것을 지운다.** 지우지 않으면 두 채널
 * 전의 그림이 남아 「이 채널이 저 그림에서 시작한다」로 보인다.
 */
export function captureFreezeFrame(sources: readonly HTMLVideoElement[]): boolean {
  const index = pickFreezeSource(
    sources.map((el) => ({
      opacity: el.style.opacity,
      videoWidth: el.videoWidth,
      readyState: el.readyState,
      paused: el.paused,
    })),
  );
  const el = index === null ? null : sources[index];
  if (!el) {
    active = false;
    return false;
  }
  const c = surface();
  c.width = el.videoWidth;
  c.height = el.videoHeight;
  const ctx = c.getContext('2d');
  if (ctx === null) {
    active = false;
    return false;
  }
  // **오염돼도 던지지 않는다** — 던지는 것은 읽을 때다(위 ★★). `try` 는 방어일 뿐이다.
  try {
    ctx.drawImage(el, 0, 0, c.width, c.height);
  } catch {
    active = false;
    return false;
  }
  heldSince = performance.now();
  active = true;
  return true;
}

export function freezeFrameHeld(): boolean {
  return active;
}

export function freezeFrameSurface(): HTMLCanvasElement {
  return surface();
}

export function freezeFrameHeldMs(): number {
  return performance.now() - heldSince;
}

export function releaseFreezeFrame(): void {
  active = false;
  canvas?.remove();
}
